export function getUserTimeZone() {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

// Method to convert UTC time to local time
export function convertUtcToLocal(utcDateTimeString, userTimeZone) {
    try {
        let isoString = utcDateTimeString;
        if (!utcDateTimeString.endsWith('Z') && !utcDateTimeString.includes('+')) {
            isoString = utcDateTimeString + 'Z';
        }
        
        const utcDateTime = new Date(isoString);
        const localDateTime = new Date(utcDateTime.toLocaleString('en-US', { timeZone: userTimeZone }));
        return localDateTime;
    } catch (error) {
        console.error('Error converting UTC to local time:', error);
        return new Date(utcDateTimeString);
    }
}

export function convertUtcToLocalTime(utcDateTimeString, userTimeZone) {
    try {
        let isoString = utcDateTimeString;
        if (!utcDateTimeString.endsWith('Z') && !utcDateTimeString.includes('+')) {
            isoString = utcDateTimeString + 'Z';
        }
        
        const utcDateTime = new Date(isoString);
        const localDateTime = new Date(utcDateTime.toLocaleString('en-US', { timeZone: userTimeZone }));

        // Format the date and time in 12-hour format
        const formattedDateTime = localDateTime.toLocaleString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: true
        });

        return formattedDateTime;
    } catch (error) {
        console.error('Error converting UTC to local time:', error);
        return utcDateTimeString;
    }
}

export function convertUtcToLocalTime24hr(utcDateTimeString, userTimeZone) {
    try {
        let isoString = utcDateTimeString;
        if (!utcDateTimeString.endsWith('Z') && !utcDateTimeString.includes('+')) {
            isoString = utcDateTimeString + 'Z';
        }
        
        const utcDateTime = new Date(isoString);
        const localDateTime = new Date(utcDateTime.toLocaleString('en-US', { timeZone: userTimeZone }));

        // Format the date and time in 24-hour format
        const formattedDateTime = localDateTime.toLocaleString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false
        });

        return formattedDateTime;
    } catch (error) {
        console.error('Error converting UTC to local time (24hr):', error);
        return utcDateTimeString;
    }
}

export function convertUtcToLocaldateTime(currentDate) {
    try {
        const formattedDateTime = currentDate.toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: true
        });
        return formattedDateTime;
    } catch (error) {
        console.error('Error formatting datetime:', error);
        return currentDate.toString();
    }
}

export const convertUtcDateToLocalDate = (utcDateTimeString, userTimeZone) => {
    try {
        let isoString = utcDateTimeString;
        if (!utcDateTimeString.endsWith('Z') && !utcDateTimeString.includes('+')) {
            isoString = utcDateTimeString + 'Z';
        }
        
        const utcDateTime = new Date(isoString);
        const localDateTime = new Date(utcDateTime.toLocaleString('en-US', { timeZone: userTimeZone }));
        const year = localDateTime.getFullYear();
        const month = String(localDateTime.getMonth() + 1).padStart(2, '0');
        const day = String(localDateTime.getDate()).padStart(2, '0');

        const formattedDateTime = `${year}-${month}-${day}`;
        
        return formattedDateTime;
    } catch (error) {
        console.error('Error converting UTC to local date:', error);
        return utcDateTimeString;
    }
};

// Utility: convert local date/time string (from input fields) to UTC ISO8601 string for API submission
export function parseLocalDateTimeToUtc(localDateStr, timeStr) {
    try {
        // localDateStr: 'YYYY-MM-DD', timeStr: 'HH:mm'
        const [year, month, day] = localDateStr.split('-').map(Number);
        const [hour, minute] = timeStr.split(':').map(Number);
        const local = new Date(year, month - 1, day, hour, minute, 0);
        return local.toISOString(); // always Z
    } catch (error) {
        console.error('Error converting local to UTC:', error);
        return localDateStr + 'T' + timeStr + ':00Z';
    }
}

// Format datetime for display with timezone info
export function formatDateTimeWithTimezone(dateTimeString, timezone) {
    try {
        const date = new Date(dateTimeString);
        const formatter = new Intl.DateTimeFormat('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            timeZone: timezone,
            timeZoneName: 'short'
        });
        
        return formatter.format(date);
    } catch (error) {
        console.error('Error formatting datetime with timezone:', error);
        return dateTimeString;
    }
}

/**
 * Convert local date and time to UTC ISO string
 * Properly accounts for timezone offset
 * @param {string} localDate - Date in format YYYY-MM-DD
 * @param {string} localTime - Time in format HH:mm
 * @param {string} userTimeZone - Timezone identifier (e.g., 'Asia/Kolkata')
 * @returns {string} UTC ISO string with Z suffix
 */
export function convertLocalDateTimeToUtc(localDate, localTime, userTimeZone) {
    try {
        // Create a date string in the user's timezone
        const [year, month, day] = localDate.split('-').map(Number);
        const [hour, minute] = localTime.split(':').map(Number);

        // Create a date object representing the local time
        const localDateTime = new Date(year, month - 1, day, hour, minute, 0);

        // Get the UTC time by converting through the timezone
        const formatter = new Intl.DateTimeFormat('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false,
            timeZone: userTimeZone
        });

        const parts = formatter.formatToParts(localDateTime);
        const tzYear = parts.find(p => p.type === 'year').value;
        const tzMonth = parts.find(p => p.type === 'month').value;
        const tzDay = parts.find(p => p.type === 'day').value;
        const tzHour = parts.find(p => p.type === 'hour').value;
        const tzMinute = parts.find(p => p.type === 'minute').value;
        const tzSecond = parts.find(p => p.type === 'second').value;

        // Calculate offset
        const tzDate = new Date(tzYear, tzMonth - 1, tzDay, tzHour, tzMinute, tzSecond);
        const offset = localDateTime.getTime() - tzDate.getTime();

        // Create UTC date
        const utcDate = new Date(localDateTime.getTime() - offset);
        return utcDate.toISOString();
    } catch (error) {
        console.error('Error converting local to UTC:', error);
        return new Date(localDate + 'T' + localTime + ':00Z').toISOString();
    }
}

/**
 * Get timezone abbreviation (e.g., IST, EST)
 * @param {string} userTimeZone - Timezone identifier
 * @returns {string} Timezone abbreviation
 */
export function getTimezoneAbbreviation(userTimeZone) {
    try {
        const formatter = new Intl.DateTimeFormat('en-US', {
            timeZoneName: 'short',
            timeZone: userTimeZone
        });

        const parts = formatter.formatToParts(new Date());
        const tzNamePart = parts.find(p => p.type === 'timeZoneName');
        return tzNamePart ? tzNamePart.value : 'UTC';
    } catch (error) {
        console.error('Error getting timezone abbreviation:', error);
        return 'UTC';
    }
}

// API CONTRACT: the backend expects/returns all datetimes in UTC ISO8601 (with 'Z'). Use parseLocalDateTimeToUtc when posting; use UTC->local utils for display.